import { createContext, useContext } from 'react';
import type { GamePlayer, MockGameState } from './mockGameTypes';

export type RuntimeMode = 'mock' | 'server';
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Unified hook surface shared by mock and server providers. */
export interface GameContextValue {
  runtimeMode: RuntimeMode;
  connectionStatus: ConnectionStatus;
  state: MockGameState;
  currentPlayer: GamePlayer | undefined;
  /** Last server/action error for toast display. */
  lastError: string | null;
  clearJoinError: () => void;
  clearError: () => void;
  createRoom: (roomName: string, nickname: string, settings: Partial<MockGameState['settings']>) => void;
  joinRoom: (roomCode: string, nickname: string) => boolean;
  kickPlayer?: (playerId: string) => void;
  toggleReady: () => void;
  startGame: () => void;
  toggleCard: (id: string) => void;
  clearSelection: () => void;
  setWriteIn: (text: string) => void;
  submitAnswer: () => void;
  confirmWriteIn: () => boolean;
  pickWinner: (playerId: string) => void;
  pickWinnerByIndex: (revealIndex: number) => void;
  nextRound: () => void;
  playAgain: () => void;
  newRoom: () => void;
  reset: () => void;
}

export const GameContext = createContext<GameContextValue | null>(null);

export function useGame(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used within a GameProvider');
  return ctx;
}

/** @deprecated Use useGame() — kept for any external references. */
export const useMockGame = useGame;
